const BITRATE_CHANGE_SENSITIVITY_DELTA_KBITS = 10;
const BITS_IN_KBIT = 1000.0;
const LOST_PACKETS_DELTA = 1;
const DISCARDED_PACKETS_DELTA = 1;
const JITTER_DELTA = 0.1;
const FRACTION_LOST_DELTA = 0.01;
const ROUNDTRIP_TIME_DELTA = 0.1;

const isValueChangeBeyondThreshold = (oldValue, newValue, threshold) => {
  if (oldValue === undefined && newValue !== undefined) {
    return true;
  }
  if (oldValue !== undefined && newValue === undefined) {
    return true;
  }

  if (oldValue && newValue && (Math.abs(newValue - oldValue) > threshold)) {
    return true;
  }

  return false;
};

class PeerConnectionQualityMonitor {
  constructor(onConnectionParametersChanged) {
    if (onConnectionParametersChanged && (typeof onConnectionParametersChanged !== 'function')) {
      throw Error('The \'onConnectionParametersChanged\' parameter value is not a function');
    }

    this.onConnectionParametersChanged = onConnectionParametersChanged;
    this.resetStats();
  }

  resetStats() {
    this.localCandidateType = undefined;
    this.remoteCandidateType = undefined;
    this.localNetworkType = undefined;
    this.remoteNetworkType = undefined;
    this.localProtocol = undefined;
    this.remoteProtocol = undefined;
    this.incomingBitrate = undefined;
    this.outgoingBitrate = undefined;
    this.receivedAudioStream = {
      packetsLost: undefined,
      packetsDiscarded: undefined,
      jitter: undefined,
    };
    this.receivedVideoStream = {
      packetsLost: undefined,
      packetsDiscarded: undefined,
      jitter: undefined,
    };
    this.sentAudioStream = {
      packetsLost: undefined,
      packetsDiscarded: undefined,
      jitter: undefined,
      fractionLost: undefined,
      roundTripTime: undefined,
    };
    this.sentVideoStream = {
      packetsLost: undefined,
      packetsDiscarded: undefined,
      jitter: undefined,
      fractionLost: undefined,
      roundTripTime: undefined,
    };
  }

  addStatsSample(connectionStats) {
    const qualityChanges = [];

    if (connectionStats.localCandidateType && (this.localCandidateType !== connectionStats.localCandidateType)) {
      qualityChanges.push(`Local ICE candidate type has changed to: ${connectionStats.localCandidateType}`);
      this.localCandidateType = connectionStats.localCandidateType;
    }
    if (connectionStats.remoteCandidateType && (this.remoteCandidateType !== connectionStats.remoteCandidateType)) {
      qualityChanges.push(`Remote ICE candidate type has changed to: ${connectionStats.remoteCandidateType}`);
      this.remoteCandidateType = connectionStats.remoteCandidateType;
    }

    if (connectionStats.localNetworkType && (this.localNetworkType !== connectionStats.localNetworkType)) {
      qualityChanges.push(`Local ICE candidate network type has changed to: ${connectionStats.localNetworkType}`);
      this.localNetworkType = connectionStats.localNetworkType;
    }
    if (connectionStats.remoteNetworkType && (this.remoteNetworkType !== connectionStats.remoteNetworkType)) {
      qualityChanges.push(`Remote ICE candidate network type has changed to: ${connectionStats.remoteNetworkType}`);
      this.remoteNetworkType = connectionStats.remoteNetworkType;
    }

    if (connectionStats.localProtocol && (this.localProtocol !== connectionStats.localProtocol)) {
      qualityChanges.push(`Local ICE candidate protocol has changed to: ${connectionStats.localProtocol}`);
      this.localProtocol = connectionStats.localProtocol;
    }
    if (connectionStats.remoteProtocol && (this.remoteProtocol !== connectionStats.remoteProtocol)) {
      qualityChanges.push(`Remote ICE candidate protocol has changed to: ${connectionStats.remoteProtocol}`);
      this.remoteProtocol = connectionStats.remoteProtocol;
    }

    if (connectionStats.incomingBitrate) {
      const incomingBitrateKBits = connectionStats.incomingBitrate / BITS_IN_KBIT;
      if (isValueChangeBeyondThreshold(this.incomingBitrate, incomingBitrateKBits, BITRATE_CHANGE_SENSITIVITY_DELTA_KBITS)) {
        qualityChanges.push(`Incoming bitrate has changed to: ${incomingBitrateKBits.toFixed(2)}`);
        this.incomingBitrate = incomingBitrateKBits;
      }
    }
    if (connectionStats.outgoingBitrate) {
      const outgoingBitrateKBits = connectionStats.outgoingBitrate / BITS_IN_KBIT;
      if (isValueChangeBeyondThreshold(this.outgoingBitrate, outgoingBitrateKBits, BITRATE_CHANGE_SENSITIVITY_DELTA_KBITS)) {
        qualityChanges.push(`Outgoing bitrate has changed to: ${outgoingBitrateKBits.toFixed(2)}`);
        this.outgoingBitrate = outgoingBitrateKBits;
      }
    }

    if (connectionStats.receivedAudio) {
      if (connectionStats.receivedAudio.packetsLost) {
        if (isValueChangeBeyondThreshold(
          this.receivedAudioStream.packetsLost, connectionStats.receivedAudio.packetsLost, LOST_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Received audio packets lost: ${connectionStats.receivedAudio.packetsLost}`);
          this.receivedAudioStream.packetsLost = connectionStats.receivedAudio.packetsLost;
        }
      }
      if (connectionStats.receivedAudio.packetsDiscarded) {
        if (isValueChangeBeyondThreshold(
          this.receivedAudioStream.packetsDiscarded, connectionStats.receivedAudio.packetsDiscarded, DISCARDED_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Received audio packets discarded: ${connectionStats.receivedAudio.packetsDiscarded}`);
          this.receivedAudioStream.packetsDiscarded = connectionStats.receivedAudio.packetsDiscarded;
        }
      }
      if (connectionStats.receivedAudio.jitter) {
        if (isValueChangeBeyondThreshold(
          this.receivedAudioStream.jitter, connectionStats.receivedAudio.jitter, JITTER_DELTA,
        )) {
          qualityChanges.push(`Received audio jitter has changed to: ${connectionStats.receivedAudio.jitter}`);
          this.receivedAudioStream.jitter = connectionStats.receivedAudio.jitter;
        }
      }
    }

    if (connectionStats.receivedVideo) {
      if (connectionStats.receivedVideo.packetsLost) {
        if (isValueChangeBeyondThreshold(
          this.receivedVideoStream.packetsLost, connectionStats.receivedVideo.packetsLost, LOST_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Received video packets lost: ${connectionStats.receivedVideo.packetsLost}`);
          this.receivedVideoStream.packetsLost = connectionStats.receivedVideo.packetsLost;
        }
      }
      if (connectionStats.receivedVideo.packetsDiscarded) {
        if (isValueChangeBeyondThreshold(
          this.receivedVideoStream.packetsDiscarded, connectionStats.receivedVideo.packetsDiscarded, DISCARDED_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Received video packets discarded: ${connectionStats.receivedVideo.packetsDiscarded}`);
          this.receivedVideoStream.packetsDiscarded = connectionStats.receivedVideo.packetsDiscarded;
        }
      }
      if (connectionStats.receivedVideo.jitter) {
        if (isValueChangeBeyondThreshold(
          this.receivedVideoStream.jitter, connectionStats.receivedVideo.jitter, JITTER_DELTA,
        )) {
          qualityChanges.push(`Received video jitter has changed to: ${connectionStats.receivedVideo.jitter}`);
          this.receivedVideoStream.jitter = connectionStats.receivedVideo.jitter;
        }
      }
    }

    if (connectionStats.sentAudio) {
      if (connectionStats.sentAudio.packetsLost) {
        if (isValueChangeBeyondThreshold(
          this.sentAudioStream.packetsLost, connectionStats.sentAudio.packetsLost, LOST_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Sent audio packets lost: ${connectionStats.sentAudio.packetsLost}`);
          this.sentAudioStream.packetsLost = connectionStats.sentAudio.packetsLost;
        }
      }
      if (connectionStats.sentAudio.packetsDiscarded) {
        if (isValueChangeBeyondThreshold(
          this.sentAudioStream.packetsDiscarded, connectionStats.sentAudio.packetsDiscarded, DISCARDED_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Sent audio packets discarded: ${connectionStats.sentAudio.packetsDiscarded}`);
          this.sentAudioStream.packetsDiscarded = connectionStats.sentAudio.packetsDiscarded;
        }
      }
      if (connectionStats.sentAudio.jitter) {
        if (isValueChangeBeyondThreshold(
          this.sentAudioStream.jitter, connectionStats.sentAudio.jitter, JITTER_DELTA,
        )) {
          qualityChanges.push(`Sent audio jitter has changed to: ${connectionStats.sentAudio.jitter}`);
          this.sentAudioStream.jitter = connectionStats.sentAudio.jitter;
        }
      }
      if (connectionStats.sentAudio.fractionLost) {
        if (isValueChangeBeyondThreshold(
          this.sentAudioStream.fractionLost, connectionStats.sentAudio.fractionLost, FRACTION_LOST_DELTA,
        )) {
          qualityChanges.push(`Sent audio fraction lost has changed to: ${connectionStats.sentAudio.fractionLost}`);
          this.sentAudioStream.fractionLost = connectionStats.sentAudio.fractionLost;
        }
      }
      if (connectionStats.sentAudio.roundTripTime) {
        if (isValueChangeBeyondThreshold(
          this.sentAudioStream.roundTripTime, connectionStats.sentAudio.roundTripTime, ROUNDTRIP_TIME_DELTA,
        )) {
          qualityChanges.push(`Sent audio round trip time has changed to: ${connectionStats.sentAudio.roundTripTime}`);
          this.sentAudioStream.roundTripTime = connectionStats.sentAudio.roundTripTime;
        }
      }
    }

    if (connectionStats.sentVideo) {
      if (connectionStats.sentVideo.packetsLost) {
        if (isValueChangeBeyondThreshold(
          this.sentVideoStream.packetsLost, connectionStats.sentVideo.packetsLost, LOST_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Sent video packets lost: ${connectionStats.sentVideo.packetsLost}`);
          this.sentVideoStream.packetsLost = connectionStats.sentVideo.packetsLost;
        }
      }
      if (connectionStats.sentVideo.packetsDiscarded) {
        if (isValueChangeBeyondThreshold(
          this.sentVideoStream.packetsDiscarded, connectionStats.sentVideo.packetsDiscarded, DISCARDED_PACKETS_DELTA,
        )) {
          qualityChanges.push(`Sent video packets discarded: ${connectionStats.sentVideo.packetsDiscarded}`);
          this.sentVideoStream.packetsDiscarded = connectionStats.sentVideo.packetsDiscarded;
        }
      }
      if (connectionStats.sentVideo.jitter) {
        if (isValueChangeBeyondThreshold(
          this.sentVideoStream.jitter, connectionStats.sentVideo.jitter, JITTER_DELTA,
        )) {
          qualityChanges.push(`Sent video jitter has changed to: ${connectionStats.sentVideo.jitter}`);
          this.sentVideoStream.jitter = connectionStats.sentVideo.jitter;
        }
      }
      if (connectionStats.sentVideo.fractionLost) {
        if (isValueChangeBeyondThreshold(
          this.sentVideoStream.fractionLost, connectionStats.sentVideo.fractionLost, FRACTION_LOST_DELTA,
        )) {
          qualityChanges.push(`Sent video fraction lost has changed to: ${connectionStats.sentVideo.fractionLost}`);
          this.sentVideoStream.fractionLost = connectionStats.sentVideo.fractionLost;
        }
      }
      if (connectionStats.sentVideo.roundTripTime) {
        if (isValueChangeBeyondThreshold(
          this.sentVideoStream.roundTripTime, connectionStats.sentVideo.roundTripTime, ROUNDTRIP_TIME_DELTA,
        )) {
          qualityChanges.push(`Sent video round trip time has changed to: ${connectionStats.sentVideo.roundTripTime}`);
          this.sentVideoStream.roundTripTime = connectionStats.sentVideo.roundTripTime;
        }
      }
    }

    if ((qualityChanges.length > 0) && this.onConnectionParametersChanged) {
      this.onConnectionParametersChanged(qualityChanges);
    }
  }

  processStatsReport(stats) {
    let currentPair;
    const statsSample = {};

    stats.forEach((report) => {
      if (report.type === 'transport') {
        currentPair = stats.get(report.selectedCandidatePairId);
      } else if (report.type === 'candidate-pair' && report.selected) {
        currentPair = report;
      } else if (report.type === 'inbound-rtp') {
        if (report.kind === 'video') {
          statsSample.receivedVideo = {
            packetsLost: report.packetsLost,
            packetsDiscarded: report.packetsDiscarded,
            jitter: report.jitter,
          };
        } else if (report.kind === 'audio') {
          statsSample.receivedAudio = {
            packetsLost: report.packetsLost,
            packetsDiscarded: report.packetsDiscarded,
            jitter: report.jitter,
          };
        }
      } else if (report.type === 'remote-inbound-rtp') {
        if (report.kind === 'video') {
          statsSample.sentVideo = {
            packetsLost: report.packetsLost,
            packetsDiscarded: report.packetsDiscarded,
            jitter: report.jitter,
            fractionLost: report.fractionLost,
            roundTripTime: report.roundTripTime,
          };
        } else if (report.kind === 'audio') {
          statsSample.sentAudio = {
            packetsLost: report.packetsLost,
            packetsDiscarded: report.packetsDiscarded,
            jitter: report.jitter,
            fractionLost: report.fractionLost,
            roundTripTime: report.roundTripTime,
          };
        }
      }
    });

    if (currentPair) {
      statsSample.incomingBitrate = currentPair.availableIncomingBitrate;
      statsSample.outgoingBitrate = currentPair.availableOutgoingBitrate;

      if (currentPair.remoteCandidateId && currentPair.localCandidateId) {
        const remoteCandidate = stats.get(currentPair.remoteCandidateId);
        const localCandidate = stats.get(currentPair.localCandidateId);

        statsSample.localCandidateType = localCandidate.candidateType;
        statsSample.localNetworkType = localCandidate.networkType;
        statsSample.localProtocol = localCandidate.protocol;

        statsSample.remoteCandidateType = remoteCandidate.candidateType;
        statsSample.remoteNetworkType = remoteCandidate.networkType;
        statsSample.remoteProtocol = remoteCandidate.protocol;
      }
    }

    this.addStatsSample(statsSample);
  }
}

export default PeerConnectionQualityMonitor;



// WEBPACK FOOTER //
// ./utils/connection-quality-monitoring.js